import { motion, AnimatePresence } from 'framer-motion' // Add this import
import { type ReactNode, useRef, useEffect } from 'react'
import { cn } from '#app/shared/lib/misc.js'
import { Separator } from '../data-display/Separator.tsx'
import { Icon } from '../icons/Icon.tsx'

export type DialogProps = {
  onClose: () => void
  open: boolean
  children: ReactNode
  className?: string
  containerClassName?: string
  variant?: 'center' | 'bottom' | 'left' | 'fullscreen'
  hideIcon?: boolean
  iconClassName?: string
  overlayClassName?: string
  overlayContent?: ReactNode
  closeOnOutsideClick?: boolean // New prop
}

const Dialog = ({
  onClose,
  open,
  children,
  className,
  containerClassName,
  overlayClassName,
  iconClassName,
  hideIcon,
  variant = 'center',
  overlayContent,
  closeOnOutsideClick = true, // Default to true for backwards compatibility
}: DialogProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (open && !dialog.open) {
      dialog.showModal()
      document.documentElement.classList.add('overflow-hidden')
    } else if (!open && dialog.open) {
      dialog.close()
      document.documentElement.classList.remove('overflow-hidden')
    }

    let mouseDownOnDialog = false

    const handleMouseDown = (event: MouseEvent) => {
      if (!closeOnOutsideClick) return
      mouseDownOnDialog = event.target === dialog
    }

    const handleMouseUp = (event: MouseEvent) => {
      if (!closeOnOutsideClick) return
      if (mouseDownOnDialog && event.target === dialog) {
        onClose()
      }
      mouseDownOnDialog = false
    }

    if (open) {
      dialog.addEventListener('mousedown', handleMouseDown)
      dialog.addEventListener('mouseup', handleMouseUp)
    }

    // Clean up the event listeners
    return () => {
      dialog.removeEventListener('mousedown', handleMouseDown)
      dialog.removeEventListener('mouseup', handleMouseUp)
      document.documentElement.classList.remove('overflow-hidden')
    }
  }, [open])

  const handleClose = () => {
    onClose()
  }

  const centerVariant = 'inset-0 m-auto w-full max-w-[40rem]'
  const bottomVariant = 'bottom-0 left-0 right-0 !w-full'
  const leftVariant = 'bottom-0 left-0 top-0 m-0 h-screen w-full max-w-none'
  const fullScreenVariant =
    'fixed inset-0 !w-screen !h-screen !max-h-screen !max-w-none rounded-none'

  return (
    <AnimatePresence>
      {open && (
        <motion.dialog
          ref={dialogRef}
          onClose={handleClose}
          initial={
            variant === 'left'
              ? { x: '-100%', opacity: 1 }
              : { scale: 0.9, opacity: 0 }
          }
          animate={
            variant === 'left' ? { x: 0, opacity: 1 } : { scale: 1, opacity: 1 }
          }
          exit={
            variant === 'left' ? { x: '-100%', opacity: 1 } : { opacity: 0 }
          }
          transition={{ duration: 0.3 }}
          className={cn(
            `
              fixed z-40 grid max-h-[100vh] gap-4 overflow-hidden border
              bg-neutral-950/80 p-0 shadow-lg duration-0

              sm:w-[calc(100%-24px)] sm:rounded-lg
            `,
            variant === 'fullscreen'
              ? fullScreenVariant
              : variant === 'center'
                ? centerVariant
                : variant === 'bottom'
                  ? bottomVariant
                  : leftVariant,
            className,
          )}
        >
          <div
            className={cn(
              `
                h-full max-h-[100vh] space-y-0 overflow-hidden p-6

                sm:max-h-[calc(100vh-24px)]
              `,
              variant === 'fullscreen' &&
                '!h-screen !max-h-screen !w-screen p-0',
              containerClassName,
            )}
          >
            <div
              className={cn(
                'flex flex-col',
                variant === 'fullscreen' && 'h-full',
              )}
            >
              {children}
            </div>
          </div>
          {!hideIcon && (
            <button
              onClick={handleClose}
              className={cn(
                'absolute right-2 top-2 cursor-pointer rounded-full p-1',
              )}
              aria-label="Close"
            >
              <Icon name="basic/x" className={iconClassName} />
            </button>
          )}
          {overlayContent && (
            <div className={cn('fixed inset-0 z-[-1]', overlayClassName)}>
              {overlayContent}
            </div>
          )}
        </motion.dialog>
      )}
    </AnimatePresence>
  )
}

const DialogHeader = ({
  variant = 'default',
  children,
}: {
  variant?: 'default' | 'separator-left' | 'separator-bottom'
  children: ReactNode
}) => {
  return variant === 'separator-left' ? (
    <div>
      <Separator textAlign="left" className="bg-foreground">
        <h2 className="text-h5 font-bold text-foreground">{children}</h2>
      </Separator>
    </div>
  ) : variant === 'separator-bottom' ? (
    <>
      <div
        className={`
          flex items-center justify-center py-4 font-semibold uppercase
          text-foreground
        `}
      >
        {children}
      </div>
      <Separator className="!mt-0" />
    </>
  ) : (
    <div className="flex justify-center font-bold text-foreground">
      {children}
    </div>
  )
}

const DialogTitle = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  return (
    <h2
      className={cn(
        'text-lg font-semibold leading-none tracking-tight',
        className,
      )}
    >
      {children}
    </h2>
  )
}

const DialogDescription = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  return (
    <p className={cn('text-sm text-foreground-muted', className)}>{children}</p>
  )
}

export { Dialog, DialogHeader, DialogTitle, DialogDescription }
